import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceArea, ReferenceLine,
} from 'recharts';
import { apiFetch } from '../api/client';
import { useSpectralMarkers, PLACEMENT_COLOUR, type AmpSample } from '../hooks/useSpectralMarkers';

// ---------------------------------------------------------------------------
// Trend "Compare" mode — baseline vs escalation mean-FFT.
//
// The user drags two windows (A baseline, B escalation) across a trend chart;
// each window is reduced by the backend to an RMS-averaged spectrum
// (POST /measurements/mean-fft) over its qualifying TWF captures. This module
// owns the selection state, the shaded ReferenceArea overlay, and the compare
// panel (waterfall of the two mean traces / positives-only difference).
//
// Colours (validated categorical pair, dataviz skill): baseline = blue #2a78d6,
// escalation = orange #eb6834. Difference (excess that appeared in B) = orange.
// ---------------------------------------------------------------------------

export const COMPARE_A = '#2a78d6';   // baseline (muted, reference)
export const COMPARE_B = '#eb6834';   // escalation (accent, period of interest)

export type CompareSignal = 'accel' | 'velocity' | 'impact';

/** Map the active primary trend tab (unit_code) to a mean-fft signal. Returns
 *  null for tabs the endpoint does not support (envelope, anomaly, health). */
export function signalFromUnit(unit: number): CompareSignal | null {
  if (unit === 0) return 'accel';
  if (unit === 2) return 'velocity';
  if (unit === -1) return 'impact';
  return null;
}

interface Win { startLabel: string; endLabel: string; }

interface MeanFft {
  axis_id: number; axis_label: string; point_name: string; machine_id: string;
  signal: string; sample_rate: number | null;
  freq_hz: number[]; freq_cpm: number[]; magnitude: number[]; unit: string;
  n_total: number; n_included: number; n_gated_out: number; n_gate_unknown: number;
  n_sr_skipped: number; n_unprocessable: number; capture_ids: number[];
  window_start: string; window_end: string;
}

// ---- label <-> timestamp helpers ------------------------------------------
// Trend x-axis values are bucket labels ('YYYY-MM-DD' for daily/weekly). A
// window spans from the first selected bucket to the last; for weekly buckets
// (trendDays > 30) the last bucket covers 7 days, so extend the end to include
// the whole bucket. measured_at is Eastern wall-clock 'YYYY/MM/DD HH:MM:SS'.
function bucketDaysFor(trendDays: number): number {
  return trendDays > 30 ? 7 : 1;   // weekly vs daily (hourly <=1d handled below)
}
function datePart(label: string): string {
  return String(label).split(/[ T]/)[0];   // strip any time component
}
function shiftDate(ymd: string, days: number): string {
  const p = ymd.split('-').map(Number);
  if (p.length !== 3 || p.some(isNaN)) return ymd.replace(/-/g, '/');
  const dt = new Date(Date.UTC(p[0], p[1] - 1, p[2]));
  dt.setUTCDate(dt.getUTCDate() + days);
  const z = (n: number) => String(n).padStart(2, '0');
  return `${dt.getUTCFullYear()}/${z(dt.getUTCMonth() + 1)}/${z(dt.getUTCDate())}`;
}
function labelsToRange(win: Win, trendDays: number): { start: string; end: string } {
  const s = datePart(win.startLabel), e = datePart(win.endLabel);
  const start = `${s.replace(/-/g, '/')} 00:00:00`;
  const end = `${shiftDate(e, bucketDaysFor(trendDays) - 1)} 23:59:59`;
  return { start, end };
}
function rangesOverlap(a: Win, b: Win): boolean {
  const as = datePart(a.startLabel), ae = datePart(a.endLabel);
  const bs = datePart(b.startLabel), be = datePart(b.endLabel);
  return as <= be && bs <= ae;
}
function fmtWin(win: Win): string {
  const s = datePart(win.startLabel), e = datePart(win.endLabel);
  return s === e ? s : `${s} → ${e}`;
}

// ---------------------------------------------------------------------------
// Selection state + drag handlers (spread onto the existing trend <LineChart>).
// ---------------------------------------------------------------------------
export interface TrendCompareState {
  compareMode: boolean;
  setCompareMode: (v: boolean) => void;
  windowA: Win | null;
  windowB: Win | null;
  next: 'A' | 'B';
  clear: (which: 'A' | 'B') => void;
  // spread onto <LineChart> — no-ops unless compareMode is on:
  onMouseDown: (e: any) => void;
  onMouseMove: (e: any) => void;
  onMouseUp: () => void;
  // in-progress drag band, if any:
  drag: { x1: string; x2: string } | null;
}

export function useTrendCompare(): TrendCompareState {
  const [compareMode, setCompareModeRaw] = useState(false);
  const [windowA, setWindowA] = useState<Win | null>(null);
  const [windowB, setWindowB] = useState<Win | null>(null);
  const [next, setNext] = useState<'A' | 'B'>('A');
  const [dragStart, setDragStart] = useState<string | null>(null);
  const [dragEnd, setDragEnd] = useState<string | null>(null);

  const setCompareMode = (v: boolean) => {
    setCompareModeRaw(v);
    if (!v) { setWindowA(null); setWindowB(null); setNext('A'); setDragStart(null); setDragEnd(null); }
  };
  const clear = (which: 'A' | 'B') => {
    if (which === 'A') setWindowA(null); else setWindowB(null);
    setNext(which);
  };
  const onMouseDown = (e: any) => {
    if (!compareMode || !e || e.activeLabel == null) return;
    setDragStart(String(e.activeLabel)); setDragEnd(String(e.activeLabel));
  };
  const onMouseMove = (e: any) => {
    if (!compareMode || dragStart == null || !e || e.activeLabel == null) return;
    setDragEnd(String(e.activeLabel));
  };
  const onMouseUp = () => {
    if (!compareMode || dragStart == null || dragEnd == null) { setDragStart(null); setDragEnd(null); return; }
    const [a, b] = [dragStart, dragEnd].sort();   // ISO date labels sort lexically
    const win: Win = { startLabel: a, endLabel: b };
    if (next === 'A') { setWindowA(win); setNext('B'); }
    else { setWindowB(win); setNext('A'); }
    setDragStart(null); setDragEnd(null);
  };

  const drag = dragStart != null && dragEnd != null ? { x1: dragStart, x2: dragEnd } : null;
  return { compareMode, setCompareMode, windowA, windowB, next, clear,
           onMouseDown, onMouseMove, onMouseUp, drag };
}

/** ReferenceArea overlay for the trend chart. Render inside <LineChart> as
 *  `{renderCompareAreas(cmp)}`. */
export function renderCompareAreas(cmp: TrendCompareState) {
  if (!cmp.compareMode) return null;
  const areas: ReactElement[] = [];
  if (cmp.windowA) areas.push(
    <ReferenceArea key="cmp-a" x1={cmp.windowA.startLabel} x2={cmp.windowA.endLabel}
      fill={COMPARE_A} fillOpacity={0.10} stroke={COMPARE_A} strokeOpacity={0.5} ifOverflow="extendDomain" />);
  if (cmp.windowB) areas.push(
    <ReferenceArea key="cmp-b" x1={cmp.windowB.startLabel} x2={cmp.windowB.endLabel}
      fill={COMPARE_B} fillOpacity={0.12} stroke={COMPARE_B} strokeOpacity={0.5} ifOverflow="extendDomain" />);
  if (cmp.drag) areas.push(
    <ReferenceArea key="cmp-drag" x1={cmp.drag.x1} x2={cmp.drag.x2}
      fill="#64748b" fillOpacity={0.15} ifOverflow="extendDomain" />);
  // Return null (not an empty array) when there's nothing to draw, so the chart's
  // child set doesn't flip null<->[] on entering Compare mode before any drag.
  return areas.length ? areas : null;
}

// ---------------------------------------------------------------------------
// Controls: the Compare toggle + the two window chips. Render in the trend
// controls row. Only shown by the caller when the active signal is supported.
// ---------------------------------------------------------------------------
export function CompareControls({ cmp }: { cmp: TrendCompareState }) {
  const chip = (which: 'A' | 'B', win: Win | null, colour: string) => {
    const isNext = cmp.next === which && !win;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] ${win ? 'bg-white' : isNext ? 'bg-gray-50 border-dashed' : 'bg-white'}`}
        style={{ borderColor: win ? colour : '#d1d5db' }}>
        <span className="w-2 h-2 rounded-sm" style={{ background: colour }} />
        <span className="text-gray-700 font-medium">{which === 'A' ? 'Baseline' : 'Escalation'}:</span>
        {win ? (
          <>
            <span className="text-gray-600">{fmtWin(win)}</span>
            <button onClick={() => cmp.clear(which)} className="ml-0.5 text-gray-400 hover:text-gray-700" title={`Clear ${which}`}>×</button>
          </>
        ) : (
          <span className="text-gray-400 italic">{isNext ? 'drag to set' : 'not set'}</span>
        )}
      </span>
    );
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={() => cmp.setCompareMode(!cmp.compareMode)}
        className={`px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${cmp.compareMode ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
        title="Compare two time windows as mean spectra">
        {cmp.compareMode ? 'Comparing…' : 'Compare'}
      </button>
      {cmp.compareMode && (
        <>
          {chip('A', cmp.windowA, COMPARE_A)}
          {chip('B', cmp.windowB, COMPARE_B)}
          <span className="text-[10px] text-gray-400">drag on the trend to set each window</span>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compare panel — two A/B calls, waterfall + difference toggle, metadata.
// ---------------------------------------------------------------------------
type PanelProps = {
  level: 'machine' | 'point';
  signal: CompareSignal;
  trendDays: number;
  windowA: Win;
  windowB: Win;
  machineId?: string;
  dbId?: number;
  axisId?: number;
};

function decimate(a: MeanFft, b: MeanFft, xLo: number, xHi: number) {
  // Frequency axis is CPM (backend returns freq_cpm[]). Only the VISIBLE
  // [xLo, xHi] window is sampled, then reduced to ~900 peak-preserving points —
  // so zooming the x-axis in re-samples that band at finer detail.
  const axis = a.freq_cpm;
  const n = Math.min(axis.length, a.magnitude.length, b.magnitude.length);
  const pts: { x: number; a: number; b: number; d: number }[] = [];
  for (let i = 0; i < n; i++) {
    const x = axis[i];
    if (x < xLo) continue;
    if (x > xHi) break;
    pts.push({ x, a: a.magnitude[i], b: b.magnitude[i], d: Math.max(0, b.magnitude[i] - a.magnitude[i]) });
  }
  const target = 900;
  if (pts.length <= target) return pts;
  const bucket = Math.ceil(pts.length / target);
  const out: typeof pts = [];
  for (let i = 0; i < pts.length; i += bucket) {
    const slice = pts.slice(i, i + bucket);
    // keep peaks: max each series; x = frequency of the bucket's max-B bin
    let mi = 0; for (let k = 1; k < slice.length; k++) if (slice[k].b > slice[mi].b) mi = k;
    out.push({
      x: slice[mi].x,
      a: Math.max(...slice.map(s => s.a)),
      b: Math.max(...slice.map(s => s.b)),
      d: Math.max(...slice.map(s => s.d)),
    });
  }
  return out;
}

function Counts({ r }: { r: MeanFft }) {
  const pill = (label: string, n: number, warn = false) =>
    n > 0 ? <span className={`px-1.5 py-0.5 rounded ${warn ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{label} {n}</span> : null;
  return (
    <div className="flex flex-wrap gap-1 text-[10px]">
      <span className="px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-medium">{r.n_included} included</span>
      {pill('gated', r.n_gated_out, true)}
      {pill('unknown-gate', r.n_gate_unknown, true)}
      {pill('sr-skip', r.n_sr_skipped, true)}
      {pill('unprocessable', r.n_unprocessable, true)}
      <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-400">of {r.n_total}</span>
    </div>
  );
}

export function TrendComparePanel(props: PanelProps) {
  const { level, signal, trendDays, windowA, windowB, machineId, dbId, axisId } = props;
  const [a, setA] = useState<MeanFft | null>(null);
  const [b, setB] = useState<MeanFft | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'waterfall' | 'difference'>('waterfall');

  const rangeA = useMemo(() => labelsToRange(windowA, trendDays), [windowA, trendDays]);
  const rangeB = useMemo(() => labelsToRange(windowB, trendDays), [windowB, trendDays]);
  const overlap = useMemo(() => rangesOverlap(windowA, windowB), [windowA, windowB]);

  useEffect(() => {
    // Hard guard: never fetch unless BOTH windows are set. The panel is already
    // gated on windowA && windowB at its mount site; this makes the invariant
    // local so no future caller can fire the mean-fft calls on toggle alone.
    if (!windowA || !windowB) return;
    let cancelled = false;
    const post = (body: any) =>
      apiFetch<MeanFft>('/measurements/mean-fft', { method: 'POST', body: JSON.stringify(body) });
    (async () => {
      setLoading(true); setError(null); setA(null); setB(null);
      try {
        if (level === 'machine') {
          // B drives the auto-selected axis; A is then pinned to it.
          const rb = await post({ machine_id: machineId, db_id: dbId, auto_axis: true, signal, start: rangeB.start, end: rangeB.end });
          if (cancelled) return;
          const ra = await post({ axis_id: rb.axis_id, signal, start: rangeA.start, end: rangeA.end });
          if (cancelled) return;
          setB(rb); setA(ra);
        } else {
          const [ra, rb] = await Promise.all([
            post({ axis_id: axisId, signal, start: rangeA.start, end: rangeA.end }),
            post({ axis_id: axisId, signal, start: rangeB.start, end: rangeB.end }),
          ]);
          if (cancelled) return;
          setA(ra); setB(rb);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to compute comparison');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [level, signal, machineId, dbId, axisId, rangeA.start, rangeA.end, rangeB.start, rangeB.end]);

  // ── Spectral-analysis tools on the mean-FFT panel (shared useSpectralMarkers) ──
  const domainMaxCpm = signal === 'impact' ? 30000 : 300000;
  const binWidthCpm = a && a.freq_cpm.length > 1 ? (a.freq_cpm[1] - a.freq_cpm[0]) : 60;  // scalar Δf (CPM)
  const [toolsFocused, setToolsFocused] = useState(false);
  const [placeActive, setPlaceActive] = useState(false);
  const hoverCpmRef = useRef(0);
  // X-axis (frequency) zoom: null = full [0, domainMax]; drag on the chart to set.
  const [xRange, setXRange] = useState<[number, number] | null>(null);
  const zoomDragRef = useRef<{ start: number } | null>(null);
  const [zoomSel, setZoomSel] = useState<{ a: number; b: number } | null>(null);
  useEffect(() => { setXRange(null); }, [signal]);   // reset zoom when the signal changes
  // Legend visibility toggle for the waterfall traces (a=Baseline, b=Escalation).
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  // Amplitude at a frequency is read from the FULL, undecimated arrays (not the
  // ~900-pt display data): Escalation (B) in the waterfall, excess (B−A) in diff.
  const ampLookup = useCallback((cpm: number): AmpSample[] => {
    if (!a || !b) return [];
    const i = Math.min(Math.max(0, Math.round(cpm / binWidthCpm)), a.magnitude.length - 1, b.magnitude.length - 1);
    if (view === 'difference') {
      return [{ label: 'Excess (B−A)', amp: Math.max(0, b.magnitude[i] - a.magnitude[i]).toFixed(4), colour: COMPARE_B }];
    }
    return [{ label: 'Escalation (B)', amp: b.magnitude[i].toFixed(4), colour: COMPARE_B }];
  }, [a, b, view, binWidthCpm]);
  const spectral = useSpectralMarkers({ ampLookup, binRes: binWidthCpm, xMax: domainMaxCpm, active: toolsFocused || placeActive });
  // Keep keyboard active while a placement is in progress even if the mouse leaves
  // (one-render lag via effect avoids the active<->placement circular dependency).
  useEffect(() => { setPlaceActive(spectral.placement != null); }, [spectral.placement]);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const el = document.getElementById('cmp-tools-ctx');
      if (el && !el.contains(e.target as Node)) spectral.closeContextMenu();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [spectral]);

  const overlayLines = () => {
    const els: ReactElement[] = [];
    spectral.markerLines.forEach((ln, i) => els.push(
      <ReferenceLine key={`m${i}`} x={ln.cpm} stroke={ln.colour} strokeWidth={1}
        strokeDasharray={ln.dashed ? '4 3' : undefined} ifOverflow="hidden"
        label={{ value: ln.label, position: 'insideTopLeft', fontSize: 9, fill: ln.colour }} />));
    spectral.previewLines.forEach((ln, i) => els.push(
      <ReferenceLine key={`p${i}`} x={ln.cpm} stroke={ln.colour} strokeWidth={1} strokeOpacity={0.35}
        strokeDasharray="4 3" ifOverflow="hidden" />));
    if (spectral.placementCursorCpm != null) els.push(
      <ReferenceLine key="pc" x={spectral.placementCursorCpm} strokeWidth={2} ifOverflow="hidden"
        stroke={spectral.placement ? PLACEMENT_COLOUR[spectral.placement.markerType] : '#111827'} />);
    if (spectral.cursorCpm != null && !spectral.placement) els.push(
      <ReferenceLine key="rc" x={spectral.cursorCpm} stroke="#374151" strokeWidth={1} strokeDasharray="4 3" ifOverflow="hidden" />);
    if (zoomSel) els.push(
      <ReferenceArea key="zoom" x1={Math.min(zoomSel.a, zoomSel.b)} x2={Math.max(zoomSel.a, zoomSel.b)}
        fill="#64748b" fillOpacity={0.15} ifOverflow="hidden" />);
    return els;
  };
  const onChartMove = (s: any) => {
    if (s && s.activeLabel != null) {
      const cpm = Number(s.activeLabel);
      hoverCpmRef.current = cpm;
      spectral.setCursor(cpm);
      if (zoomDragRef.current) setZoomSel({ a: zoomDragRef.current.start, b: cpm });
    }
  };
  const onChartDown = (s: any) => {
    if (s && s.activeLabel != null) { zoomDragRef.current = { start: Number(s.activeLabel) }; setZoomSel(null); }
  };
  const onChartUp = () => {
    if (zoomDragRef.current && zoomSel) {
      const lo = Math.min(zoomSel.a, zoomSel.b), hi = Math.max(zoomSel.a, zoomSel.b);
      if (hi - lo > binWidthCpm * 3) setXRange([lo, hi]);   // ignore tiny drags / clicks
    }
    zoomDragRef.current = null; setZoomSel(null);
  };
  const clearZoomDrag = () => { zoomDragRef.current = null; setZoomSel(null); };
  const toggleSeries = (k?: string) => {
    if (k !== 'a' && k !== 'b') return;
    setHiddenSeries(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };
  const onToolsContextMenu = (e: React.MouseEvent) => {
    if (!a || !b) return;
    e.preventDefault();
    spectral.openContextMenu(e.clientX, e.clientY, Math.round(hoverCpmRef.current));
  };

  const shell = (children: ReactNode) => (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-3 border border-gray-200 shadow-sm mt-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-gray-900 text-sm font-medium">Compare — mean spectrum ({signal})</h3>
        {a && b && (
          <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
            {(['waterfall', 'difference'] as const).map((v, i) => (
              <button key={v} onClick={() => setView(v)}
                className={`px-2.5 py-1.5 capitalize ${i > 0 ? 'border-l border-gray-200' : ''} ${view === v ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{v}</button>
            ))}
          </div>
        )}
      </div>
      {children}
    </div>
  );

  if (loading) return shell(<div className="h-[260px] bg-gray-50 rounded animate-pulse" />);
  if (error) {
    const noCaptures = /no qualifying captures/i.test(error);
    return shell(<div className="flex items-center justify-center text-center text-sm text-gray-500 h-[120px] px-4">
      {noCaptures ? 'No qualifying captures to auto-select an axis in the escalation window (all readings below the 0.1 g running threshold, or no waveforms).' : error}
    </div>);
  }
  if (!a || !b) return null;

  // Edge states
  const tooFew = (r: MeanFft, which: string) =>
    r.n_included < 2
      ? (r.n_total > 0 && r.n_included === 0 && (r.n_gated_out + r.n_gate_unknown) === (r.n_total - r.n_unprocessable - r.n_sr_skipped)
          ? `All readings in the ${which} window are below the 0.1 g running threshold.`
          : `Not enough qualifying readings in the ${which} window (min 2 — found ${r.n_included}).`)
      : null;
  const edge = tooFew(a, 'baseline') || tooFew(b, 'escalation');

  const [xLo, xHi] = xRange ?? [0, domainMaxCpm];
  const data = decimate(a, b, xLo, xHi);
  const unit = a.unit;
  const xLabel = 'CPM';
  const freqRes = a.freq_cpm.length > 1 ? (a.freq_cpm[1] - a.freq_cpm[0]) : 0;   // Δf in CPM
  const xFmt = (v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`);

  const meta = (label: string, r: MeanFft, win: Win, colour: string) => (
    <div className="flex-1 min-w-[220px] rounded-lg border border-gray-200 p-2" style={{ borderLeft: `3px solid ${colour}` }}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">{label}</span>
        <span className="text-[11px] text-gray-500">{fmtWin(win)}</span>
      </div>
      <div className="text-[10px] text-gray-500 mt-0.5">
        {r.point_name} · {r.axis_label} · {signal} · {r.sample_rate ? `${Math.round(r.sample_rate)} Hz` : '—'} · Δf {freqRes.toFixed(1)} CPM
      </div>
      <div className="mt-1"><Counts r={r} /></div>
    </div>
  );

  return shell(
    <>
      <div className="flex flex-wrap gap-2 mb-2">
        {meta('Baseline (A)', a, windowA, COMPARE_A)}
        {meta('Escalation (B)', b, windowB, COMPARE_B)}
      </div>
      {overlap && <div className="text-[11px] text-amber-600 mb-1">⚠ The two windows overlap.</div>}
      {Math.max(a.n_included, b.n_included) > 200 &&
        <div className="text-[11px] text-gray-500 mb-1">Large window — averaging {a.n_included}/{b.n_included} captures (A/B); this may be slow.</div>}

      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[10px] text-gray-400">Drag across the spectrum to zoom the frequency axis</span>
        {xRange && (
          <button onClick={() => setXRange(null)}
            className="text-[10px] px-2 py-0.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">
            Reset zoom · {Math.round(xLo).toLocaleString()}–{Math.round(xHi).toLocaleString()} CPM
          </button>
        )}
      </div>

      <div
        onContextMenu={onToolsContextMenu}
        onMouseEnter={() => setToolsFocused(true)}
        onMouseLeave={() => { setToolsFocused(false); spectral.setCursor(null); clearZoomDrag(); }}
        style={spectral.placement ? { userSelect: 'none' } : undefined}
      >
      {edge ? (
        <div className="flex items-center justify-center text-center text-sm text-gray-500 h-[120px] px-4">{edge}</div>
      ) : view === 'waterfall' ? (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 4 }}
            onMouseDown={onChartDown} onMouseMove={onChartMove} onMouseUp={onChartUp}
            onMouseLeave={() => spectral.setCursor(null)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F2" />
            <XAxis dataKey="x" type="number" domain={[xLo, xHi]} allowDataOverflow tickFormatter={xFmt}
              stroke="#9CA3AF" tick={{ fontSize: 10 }}
              label={{ value: xLabel, position: 'insideBottomRight', offset: -2, fontSize: 10, fill: '#9CA3AF' }} />
            <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} tickFormatter={(v: number) => v.toFixed(3)}
              label={{ value: unit, angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
            <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: 11 }}
              formatter={(v: any, n: any) => [Number(v).toFixed(4) + ' ' + unit, n]}
              labelFormatter={(l: any) => `${Math.round(Number(l)).toLocaleString()} ${xLabel}`} />
            <Legend wrapperStyle={{ fontSize: 11, cursor: 'pointer' }} onClick={(o: any) => toggleSeries(o?.dataKey)} />
            <Line type="monotone" dataKey="a" name={`Baseline  ${fmtWin(windowA)}`} stroke={COMPARE_A} strokeWidth={2} dot={false} isAnimationActive={false} hide={hiddenSeries.has('a')} />
            <Line type="monotone" dataKey="b" name={`Escalation  ${fmtWin(windowB)}`} stroke={COMPARE_B} strokeWidth={2} dot={false} isAnimationActive={false} hide={hiddenSeries.has('b')} />
            {overlayLines()}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 4 }}
            onMouseDown={onChartDown} onMouseMove={onChartMove} onMouseUp={onChartUp}
            onMouseLeave={() => spectral.setCursor(null)}>
            <defs>
              <linearGradient id="cmpDiff" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={COMPARE_B} stopOpacity={0.55} />
                <stop offset="100%" stopColor={COMPARE_B} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEF0F2" />
            <XAxis dataKey="x" type="number" domain={[xLo, xHi]} allowDataOverflow tickFormatter={xFmt}
              stroke="#9CA3AF" tick={{ fontSize: 10 }}
              label={{ value: xLabel, position: 'insideBottomRight', offset: -2, fontSize: 10, fill: '#9CA3AF' }} />
            <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} tickFormatter={(v: number) => v.toFixed(3)}
              label={{ value: `excess ${unit}`, angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
            <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: 11 }}
              formatter={(v: any) => [Number(v).toFixed(4) + ' ' + unit, 'Excess (B−A)']}
              labelFormatter={(l: any) => `${Math.round(Number(l)).toLocaleString()} ${xLabel}`} />
            <Area type="monotone" dataKey="d" name="Excess (B − A)" stroke={COMPARE_B} strokeWidth={2} fill="url(#cmpDiff)" isAnimationActive={false} />
            {overlayLines()}
          </AreaChart>
        </ResponsiveContainer>
      )}
      </div>

      {/* Spectral-tools readout + markers */}
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
        {spectral.placement ? (
          <span className="text-indigo-700">
            <span className="font-semibold capitalize">{spectral.placement.markerType}</span>{' '}
            {spectral.placement.step === 'position' && '— move ← → · Enter to set · Esc'}
            {spectral.placement.step === 'spacing' && <>— spacing <span className="font-mono">{Math.round(spectral.placement.spacing).toLocaleString()} CPM</span> · ← → · Enter</>}
            {spectral.placement.step === 'count' && <>— count <span className="font-mono">{spectral.placement.count}</span> · ← → · Enter to save</>}
            {spectral.placementReadout && spectral.placementReadout.axes.length > 0 && (
              <span className="ml-2 font-mono text-indigo-900">{Math.round(spectral.placementReadout.cpm).toLocaleString()} CPM · {spectral.placementReadout.axes[0].amp} {unit}</span>
            )}
          </span>
        ) : spectral.cursorReadout && spectral.cursorReadout.length > 0 ? (
          <span className="text-gray-600 font-mono">
            {Math.round(spectral.cursorCpm!).toLocaleString()} CPM
            <span className="text-gray-400 ml-1">· {spectral.cursorReadout[0].label} {spectral.cursorReadout[0].amp} {unit}</span>
          </span>
        ) : (
          <span className="text-gray-400">Right-click the spectrum to add markers · ← → move · Enter · Esc</span>
        )}
        {spectral.markers.length > 0 && (
          <span className="flex items-center gap-1 text-gray-500">
            · {spectral.markers.length} marker{spectral.markers.length > 1 ? 's' : ''}
            <button onClick={() => spectral.removeLast()} title="Remove last" className="text-gray-400 hover:text-orange-500 px-0.5">↩</button>
            <button onClick={() => spectral.clearAll()} title="Clear all" className="text-gray-400 hover:text-red-500 px-0.5">✕</button>
          </span>
        )}
      </div>

      {spectral.contextMenu && (
        <div id="cmp-tools-ctx" className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl text-sm min-w-[190px]"
          style={{ left: spectral.contextMenu.x, top: spectral.contextMenu.y }}>
          <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100 font-mono">{Math.round(spectral.contextMenu.cpm).toLocaleString()} CPM</div>
          <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => spectral.startPlacement('standalone', spectral.contextMenu!.cpm)}>Add standalone marker</button>
          <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => spectral.startPlacement('harmonic', spectral.contextMenu!.cpm)}>Add harmonic series</button>
          <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-gray-700" onClick={() => spectral.startPlacement('sideband', spectral.contextMenu!.cpm)}>Add sideband markers</button>
          <div className="border-t border-gray-100">
            <button className="w-full text-left px-3 py-2 hover:bg-gray-50 text-red-500" onClick={() => { spectral.clearAll(); spectral.closeContextMenu(); }}>Clear all markers</button>
          </div>
        </div>
      )}

      <div className="text-[10px] text-gray-400 mt-1">
        {view === 'difference'
          ? 'Excess amplitude = max(0, escalation − baseline) per frequency bin (positives only).'
          : 'RMS-averaged spectrum per window over qualifying captures (≥ 0.1 g running gate).'}
      </div>
    </>
  );
}
